<script lang="ts">
  import { onMount } from 'svelte';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { dashboardApi } from '$lib/api/dashboard';
  import { leaveSummaryApi } from '$lib/api/leaveSummary';
  import { boardApi } from '$lib/api/board';
  import { toast } from '$lib/state/toast.svelte';
  import PunchInModal from './PunchInModal.svelte';
  import PunchOutModal from './PunchOutModal.svelte';
  import PunchBoardFollowUpModal from './PunchBoardFollowUpModal.svelte';
  import BoardCheckInPanel from './BoardCheckInPanel.svelte';
  import BoardUpdatesSheet from './BoardUpdatesSheet.svelte';
  import { boardToday, boardReminders, boardCheckInDismissalKey, boardPostCheckInVisible } from '$lib/utils/board';
  import { businessDays } from '$lib/utils/leave';
  import StatRow from '$lib/components/ui/StatRow.svelte';
  import MyDayTasks from './MyDayTasks.svelte';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { t } from '$lib/i18n';
  import type { WorkLog, BoardCard, BoardUpdateStatus } from '$lib/types';

  const queryClient = useQueryClient();

  const dashboardQuery = createQuery(() => ({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get()
  }));
  const leaveSummaryQuery = createQuery(() => ({
    queryKey: ['leave-summary'],
    queryFn: () => leaveSummaryApi.get()
  }));
  const boardQuery = createQuery(() => ({
    queryKey: ['board'],
    queryFn: () => boardApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['work-logs'] });
    queryClient.invalidateQueries({ queryKey: ['recent-remote-locations'] });
  }

  function refreshBoard() {
    queryClient.invalidateQueries({ queryKey: ['board'] });
  }

  let showPunchIn = $state(false);
  let showPunchOut = $state(false);
  let punchFollowUp = $state<'in' | 'out' | null>(null);
  let pendingCheckInFor = $state<number | null>(null);
  let completedCheckInFor = $state<number | null>(null);
  let dismissedDay = $state('');
  let currentDay = $state(boardToday());
  let updateCard = $state<BoardCard | null>(null);
  let updateStatus = $state<BoardUpdateStatus>('on_track');
  let updateText = $state('');
  onMount(() => {
    const timer = setInterval(() => { currentDay = boardToday(); }, 60_000);
    return () => clearInterval(timer);
  });
  $effect(() => {
    const key = boardCheckInDismissalKey(session.me?.id);
    try { dismissedDay = localStorage.getItem(key) ?? ''; } catch { dismissedDay = ''; }
  });
  const showCheckIn = $derived(boardPostCheckInVisible(completedCheckInFor, session.me?.id, dismissedDay, currentDay));
  const reminders = $derived(boardReminders((boardQuery.data ?? []).filter((card) => card.user_id === session.me?.id), currentDay));
  function closePunchFollowUp() {
    if (punchFollowUp === 'in' && pendingCheckInFor === session.me?.id) {
      completedCheckInFor = pendingCheckInFor;
    }
    pendingCheckInFor = null;
    punchFollowUp = null;
  }
  function dismissCheckIn() {
    dismissedDay = boardToday();
    try { localStorage.setItem(boardCheckInDismissalKey(session.me?.id), dismissedDay); } catch { /* Keep in-memory dismissal. */ }
  }
  function openCheckInUpdate(card: BoardCard, status?: BoardUpdateStatus) {
    updateStatus = status ?? (card.col === 'blocked' ? 'blocked' : card.col === 'done' ? 'done' : 'on_track');
    updateText = status ? t(`board.quick.text.${status}`) : '';
    updateCard = card;
  }

  function hoursFor(logs: WorkLog[]): number {
    const now = Date.now();
    const ms = logs.reduce((sum, l) => {
      const start = new Date(l.punch_in).getTime();
      const end = l.punch_out ? new Date(l.punch_out).getTime() : now;
      return sum + Math.max(0, end - start);
    }, 0);
    return ms / 3_600_000;
  }

  function fmtTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function onPunchSuccess(kind: 'in' | 'out', zoneName?: string, workLog?: WorkLog) {
    refresh();
    if (kind === 'in') {
      // Only this user's successful punch can trigger the post-check-in panel.
      pendingCheckInFor = workLog?.user_id === session.me?.id ? session.me?.id ?? null : null;
      toast.success(workLog?.late === true
        ? t('dashboard.toast.punchedInLate', { minutes: workLog.late_by_minutes ?? 0 })
        : zoneName ? t('dashboard.toast.punchedInZone', { zone: zoneName }) : t('dashboard.toast.punchedIn'));
      punchFollowUp = 'in';
    } else {
      toast.success(t('dashboard.toast.punchedOut'));
      punchFollowUp = 'out';
    }
  }

  const openCardCount = $derived((boardQuery.data ?? []).filter((c) => c.col !== 'done').length);
  const leaveLeft = $derived(
    (leaveSummaryQuery.data?.types ?? []).reduce((sum, lt) => sum + Math.max(0, lt.remaining), 0)
  );

</script>

<div class="tym-page">
  <header class="mb-5">
    <h1 class="font-heading text-2xl font-bold text-navy">{t('myDay.heading')}</h1>
    <p class="mt-1 text-sm text-dark-gray">{t('myDay.welcome')}</p>
  </header>
  {#if showCheckIn && !punchFollowUp && !showPunchIn && !showPunchOut}
    <BoardCheckInPanel cards={reminders} loading={boardQuery.isPending} failed={boardQuery.isError} onCreate={() => route.push({ tab: 'board', checkin: true })} onDismiss={dismissCheckIn} onUpdate={openCheckInUpdate} />
  {/if}
  {#if dashboardQuery.isLoading}
    <div class="tym-loading-state">{t('dashboard.loading')}</div>
  {:else if dashboardQuery.isError}
    <div class="tym-error-state" role="alert">{t('dashboard.error')}<button type="button" class="btn-secondary ml-3" onclick={() => dashboardQuery.refetch()}>{t('common.retry')}</button></div>
  {:else if dashboardQuery.data}
    {@const d = dashboardQuery.data}

    <div class="mb-5">
      <!-- Compact punch hero -->
      <div class="flex flex-wrap items-center gap-2 rounded-2xl bg-grad-hero p-3 md:gap-3 md:p-4 lg:p-5" style="box-shadow:var(--tym-shadow-2)">
        <div class="min-w-0 flex-1">
          <div class="mb-0.5 flex items-center gap-2 md:mb-1">
            <span class="h-1.5 w-1.5 rounded-full bg-toyama-lime md:h-2 md:w-2" style="box-shadow:0 0 0 3px rgba(151,215,0,.25)"></span>
            <span class="text-[9px] font-bold uppercase tracking-[.14em] text-white/55 md:text-[10px]">
              {#if session.me?.attendance_exempt}{t('dashboard.attendanceExempt')}{:else if d.on_leave_today && !d.current_work_log}{t('dashboard.onLeave')}{:else if d.current_work_log}{t('dashboard.onClock')}{:else}{t('dashboard.offClock')}{/if}
            </span>
          </div>
          {#if session.me?.attendance_exempt}
            <div class="font-heading text-[17px] font-bold leading-tight tracking-[-.02em] text-white md:text-[22px] lg:text-[24px]">
              {t('dashboard.attendanceNotRequired')}
            </div>
            <div class="mt-0.5 hidden text-[12px] text-white/66 md:block">{t('dashboard.attendanceExemptHint')}</div>
          {:else if d.on_leave_today && !d.current_work_log}
            <div class="font-heading text-[17px] font-bold leading-tight tracking-[-.02em] text-white md:text-[22px] lg:text-[24px]">
              {t('dashboard.onLeaveToday')}
            </div>
            <div class="mt-0.5 hidden text-[12px] text-white/66 md:block">{t('dashboard.onLeaveDisabled')}</div>
          {:else if d.current_work_log}
            <div class="font-heading text-[17px] font-bold leading-tight tracking-[-.02em] text-white md:text-[22px] lg:text-[24px]">
              {t('dashboard.inSince', { time: fmtTime(d.current_work_log.punch_in) })}
            </div>
            <div class="mt-0.5 hidden text-[12px] text-white/66 md:block">
              {#if d.current_work_log.no_site}Sales &middot;
              {:else if d.current_work_log.remote}{d.current_work_log.location_name}
                {#if d.current_work_log.location_area && d.current_work_log.location_area !== '—'}, {d.current_work_log.location_area}{/if}
                &middot;
              {:else if d.current_work_log.work_zone_name}{d.current_work_log.work_zone_name} &middot; {/if}{hoursFor([
                d.current_work_log
              ]).toFixed(1)}{t('unit.hours')}
              {t('dashboard.soFar')}
            </div>
          {:else}
            <div class="font-heading text-[17px] font-bold leading-tight tracking-[-.02em] text-white md:text-[22px] lg:text-[24px]">{t('dashboard.notPunchedIn')}</div>
            <div class="mt-0.5 hidden text-[12px] text-white md:block">{t('dashboard.punchInHint')}</div>
          {/if}
        </div>
        <div class="flex shrink-0 items-center gap-2">
          {#if !session.me?.attendance_exempt && d.current_work_log}
            <button
              type="button"
              class="!min-h-[44px] !rounded-[10px] !border-white/25 !bg-white/10 !px-5 !text-white hover:!bg-white/20 btn-secondary"
              onclick={() => (showPunchOut = true)}
            >
              {t('dashboard.punchOut')}
            </button>
          {:else if !session.me?.attendance_exempt && !d.on_leave_today}
            <button type="button" class="btn-primary !min-h-[44px] !rounded-[10px] !px-5" onclick={() => (showPunchIn = true)}>
              {t('dashboard.punchIn')}
            </button>
          {/if}
        </div>
      </div>

    </div>
  {/if}

  <MyDayTasks today={currentDay} />

  {#if dashboardQuery.data}
    {@const d = dashboardQuery.data}

    <div class="mb-5">
      <StatRow
        items={[
          { label: t('dashboard.stat.today'), value: hoursFor(d.today_work_logs).toFixed(1), unit: t('unit.hours') },
          { label: t('dashboard.stat.week'), value: hoursFor(d.week_work_logs).toFixed(1), unit: t('unit.hours') },
          { label: t('dashboard.stat.leaveLeft'), value: leaveLeft, unit: t('unit.days') },
          { label: t('dashboard.stat.openCards'), value: boardQuery.isPending || boardQuery.isError ? '—' : openCardCount }
        ]}
      />
    </div>

    <div class="mb-5 tym-card p-5">
      <p class="tym-section-title mb-3">{t('dashboard.leave')}</p>
      {#if d.current_leave}
        <p class="text-[13.5px] text-toyama-heavy-carbon">
          {t('dashboard.leaveThrough', { date: d.current_leave.end_date?.slice(0, 10) ?? '' })}
          {#if d.current_leave.end_date}
            ({businessDays(d.current_leave.start_date, d.current_leave.end_date, d.current_leave.half_day)} {t('dashboard.days')}).
          {/if}
        </p>
      {:else if d.upcoming_leave}
        <p class="text-[13.5px] text-toyama-heavy-carbon">
          {t('dashboard.upcomingLeave', { start: d.upcoming_leave.start_date, end: d.upcoming_leave.end_date ?? '' })}
        </p>
      {:else}
        <p class="text-[13.5px] text-dark-gray">{t('dashboard.noLeaveScheduled')}</p>
      {/if}
      {#if d.pending_leave_requests.length > 0}
        <p class="mt-2 text-[12.5px] text-dark-gray">
          {t('dashboard.pendingRequests', { n: d.pending_leave_requests.length })}
        </p>
      {/if}
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      {#if leaveSummaryQuery.data}
        {@const ls = leaveSummaryQuery.data}
        <section class="tym-card p-4">
          <div class="mb-3 flex items-baseline justify-between">
            <h2 class="font-heading text-sm font-bold text-navy">{t('dashboard.yourLeave')}</h2>
            <span class="text-[11px] text-dark-gray">{ls.fy_label}</span>
          </div>
          <div class="space-y-2.5">
            {#each ls.types as lt (lt.leave_type_id)}
              <div>
                <div class="mb-1 flex justify-between gap-2 text-[12.5px]">
                  <span class="text-toyama-heavy-carbon">{lt.name}</span>
                  <span class="tym-stat-value text-[12.5px]">{lt.availed} / {lt.quota}{t('unit.days')}</span>
                </div>
                <div class="h-[5px] overflow-hidden rounded-full bg-toyama-light-carbon">
                  <div
                    class="h-full rounded-full bg-toyama-teal"
                    style="width:{lt.quota > 0 ? Math.min(100, (lt.availed / lt.quota) * 100) : 0}%"
                  ></div>
                </div>
              </div>
            {:else}
              <p class="text-[13.5px] text-dark-gray">{t('dashboard.noLeaveTypes')}</p>
            {/each}
          </div>
        </section>
      {/if}

      <section class="tym-card p-4">
        <h2 class="mb-3 font-heading text-sm font-bold text-navy">{t('dashboard.announcements')}</h2>
        {#if d.announcements.length > 0}
          <div class="space-y-3">
            {#each d.announcements as a (a.id)}
              <div class="flex gap-2.5">
                <span class="w-[5px] shrink-0 rounded-full bg-toyama-teal"></span>
                <div class="min-w-0">
                  <p class="text-[12.5px] font-semibold text-navy">{a.title}</p>
                  <p class="mt-0.5 text-[11px] text-dark-gray">{new Date(a.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <p class="text-[13px] text-dark-gray">{t('dashboard.noAnnouncements')}</p>
        {/if}
      </section>
    </div>

    {#if showPunchIn && !session.me?.attendance_exempt}
      <PunchInModal onClose={() => (showPunchIn = false)} onSuccess={(workLog, zoneName) => onPunchSuccess('in', zoneName, workLog)} />
    {/if}
    {#if showPunchOut && d.current_work_log && !session.me?.attendance_exempt}
      <PunchOutModal onClose={() => (showPunchOut = false)} onSuccess={() => onPunchSuccess('out')} />
    {/if}
    {#if punchFollowUp}
      <PunchBoardFollowUpModal
        kind={punchFollowUp}
        cards={boardQuery.data ?? []}
        loading={boardQuery.isPending}
        failed={boardQuery.isError}
        onRetry={() => { void boardQuery.refetch(); }}
        onClose={closePunchFollowUp}
        onMutated={refreshBoard}
      />
    {/if}
  {/if}
</div>

{#if updateCard && completedCheckInFor === session.me?.id}
  {#key updateCard.id}<BoardUpdatesSheet card={updateCard} initialStatus={updateStatus} initialText={updateText} onClose={() => (updateCard = null)} onSaved={refreshBoard} />{/key}
{/if}
